import { Component, input, output } from '@angular/core';
import { LucideDynamicIcon, LucideMenu, LucidePanelLeftClose, LucidePanelLeftOpen, provideLucideIcons } from '@lucide/angular';
import { UiBreadcrumbsComponent, UiBreadcrumbItem } from '../breadcrumbs/ui-breadcrumbs.component';

@Component({
  selector: 'ui-topbar',
  standalone: true,
  imports: [LucideDynamicIcon, UiBreadcrumbsComponent],
  providers: [provideLucideIcons(LucideMenu, LucidePanelLeftClose, LucidePanelLeftOpen)],
  template: `
    <header class="ui-topbar" [class.ui-topbar--primary]="primary()">
      <div class="ui-topbar__start">
        <button
          type="button"
          class="ui-topbar__menu-btn"
          aria-label="Abrir menu"
          (click)="menuClick.emit()"
        >
          <svg lucideIcon="menu" [size]="22"></svg>
        </button>
        @if (showSidebarCollapse()) {
          <button
            type="button"
            class="ui-topbar__collapse-btn"
            [attr.aria-label]="sidebarCollapsed() ? 'Expandir menu' : 'Recolher menu'"
            (click)="sidebarCollapseClick.emit()"
          >
            <svg [lucideIcon]="sidebarCollapsed() ? 'panel-left-open' : 'panel-left-close'" [size]="20"></svg>
          </button>
        }
        @if (breadcrumbs().length) {
          <ui-breadcrumbs [items]="breadcrumbs()" />
        }
      </div>
      <div class="ui-topbar__search">
        <ng-content select="[search]" />
      </div>
      <div class="ui-topbar__end">
        <ng-content select="[actions]" />
      </div>
    </header>
  `,
  styles: [`
    .ui-topbar {
      display: flex;
      align-items: center;
      gap: 0.75rem 1rem;
      padding: 0.625rem 1.25rem;
      background: var(--surface-bg);
      border-bottom: 1px solid var(--border-color);
      flex-wrap: wrap;
      min-height: 3.5rem;
      box-sizing: border-box;
    }

    .ui-topbar > * {
      align-self: center;
    }

    .ui-topbar--primary {
      background: var(--topbar-bg, var(--brand-primary, #1677ff));
      border-bottom-color: var(--topbar-border, color-mix(in srgb, var(--topbar-bg, var(--brand-primary)) 75%, #000));
      color: var(--topbar-fg, var(--text-primary));
    }

    .ui-topbar__menu-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 2.25rem;
      height: 2.25rem;
      border: 1px solid var(--border-color);
      border-radius: 0.5rem;
      background: var(--surface-bg);
      color: var(--text-primary);
      cursor: pointer;
      transition: background 0.15s, border-color 0.15s, color 0.15s;
    }

    .ui-topbar__menu-btn:hover {
      background: var(--surface-muted);
      border-color: color-mix(in srgb, var(--border-color) 70%, var(--text-primary));
    }

    .ui-topbar--primary .ui-topbar__menu-btn {
      border-color: var(--topbar-border, var(--on-brand-primary-border));
      background: var(--topbar-surface, var(--on-brand-primary-subtle));
      color: var(--topbar-fg, var(--on-brand-primary));
    }

    .ui-topbar--primary .ui-topbar__menu-btn:hover {
      background: var(--topbar-surface-hover, var(--on-brand-primary-hover));
      border-color: var(--topbar-border, var(--on-brand-primary-border));
    }

    @media (width >= 64rem) {
      .ui-topbar__menu-btn {
        display: none;
      }
    }

    .ui-topbar__collapse-btn {
      display: none;
      align-items: center;
      justify-content: center;
      width: 2.25rem;
      height: 2.25rem;
      border: 1px solid var(--border-color);
      border-radius: 0.5rem;
      background: var(--surface-bg);
      color: var(--text-primary);
      cursor: pointer;
      transition: background 0.15s, border-color 0.15s, color 0.15s;
    }

    .ui-topbar__collapse-btn:hover {
      background: var(--surface-muted);
      border-color: color-mix(in srgb, var(--border-color) 70%, var(--text-primary));
    }

    @media (width >= 64rem) {
      .ui-topbar__collapse-btn {
        display: inline-flex;
      }
    }

    .ui-topbar--primary .ui-topbar__collapse-btn {
      border-color: var(--topbar-border, var(--on-brand-primary-border));
      background: var(--topbar-surface, var(--on-brand-primary-subtle));
      color: var(--topbar-fg, var(--on-brand-primary));
    }

    .ui-topbar--primary .ui-topbar__collapse-btn:hover {
      background: var(--topbar-surface-hover, var(--on-brand-primary-hover));
      border-color: var(--topbar-border, var(--on-brand-primary-border));
    }

    @media (width >= 48rem) {
      .ui-topbar {
        display: grid;
        grid-template-columns: 1fr minmax(16rem, 28rem) 1fr;
        align-items: center;
        flex-wrap: nowrap;
      }
    }

    .ui-topbar__start {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      min-width: 0;
      flex: 0 1 auto;
    }

    @media (width >= 48rem) {
      .ui-topbar__start {
        justify-self: start;
      }
    }

    .ui-topbar__search {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 1 1 12rem;
      min-width: 0;
      order: 3;
      width: 100%;
      max-width: 28rem;
      margin-inline: auto;
    }

    @media (width >= 48rem) {
      .ui-topbar__search {
        order: 0;
        width: 100%;
        grid-column: 2;
        justify-self: center;
      }
    }

    .ui-topbar__end {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-shrink: 0;
      margin-left: auto;
      order: 2;
    }

    @media (width >= 48rem) {
      .ui-topbar__end {
        order: 0;
        margin-left: 0;
        justify-self: end;
      }
    }
  `],
})
export class UiTopbarComponent {
  primary = input(true);
  breadcrumbs = input<UiBreadcrumbItem[]>([]);
  showSidebarCollapse = input(false);
  sidebarCollapsed = input(false);

  menuClick = output<void>();
  sidebarCollapseClick = output<void>();
}
